import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, type Category, type BlogPost, type DocPost } from '../lib/supabase';

interface ContentContextType {
  blogPosts: BlogPost[];
  docPosts: DocPost[];
  categories: Category[];
  loading: boolean;
  error: string | null;
  
  // Blog posts
  addBlogPost: (post: Omit<BlogPost, 'id' | 'created_at' | 'updated_at'>) => Promise<BlogPost | null>;
  updateBlogPost: (id: string, post: Partial<BlogPost>) => Promise<BlogPost | null>;
  deleteBlogPost: (id: string) => Promise<boolean>;
  getBlogPost: (id: string) => BlogPost | undefined;
  getBlogPostBySlug: (slug: string) => BlogPost | undefined;
  
  // Doc posts
  addDocPost: (post: Omit<DocPost, 'id' | 'created_at' | 'updated_at'>) => Promise<DocPost | null>;
  updateDocPost: (id: string, post: Partial<DocPost>) => Promise<DocPost | null>;
  deleteDocPost: (id: string) => Promise<boolean>;
  getDocPost: (id: string) => DocPost | undefined;
  getDocPostBySlug: (slug: string) => DocPost | undefined;
  
  // Categories
  addCategory: (category: Omit<Category, 'id' | 'created_at' | 'updated_at'>) => Promise<Category | null>;
  updateCategory: (id: string, category: Partial<Category>) => Promise<Category | null>;
  deleteCategory: (id: string) => Promise<boolean>;
  getCategory: (id: string) => Category | undefined;
  getCategoriesByType: (type: 'blog' | 'doc') => Category[];
  
  // Utility
  refreshData: () => Promise<void>;
  generateSitemap: () => void;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export const useContent = () => {
  const context = useContext(ContentContext);
  if (context === undefined) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

export const ContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [docPosts, setDocPosts] = useState<DocPost[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .order('type', { ascending: true })
        .order('order', { ascending: true });

      if (categoriesError) throw categoriesError;

      // Fetch blog posts with categories
      const { data: blogData, error: blogError } = await supabase
        .from('blog_posts')
        .select(`
          *,
          category:categories(*)
        `)
        .order('created_at', { ascending: false });

      if (blogError) throw blogError;

      // Fetch doc posts with categories
      const { data: docData, error: docError } = await supabase
        .from('doc_posts')
        .select(`
          *,
          category:categories(*)
        `)
        .order('order', { ascending: true });

      if (docError) throw docError;

      setCategories(categoriesData || []);
      setBlogPosts(blogData || []);
      setDocPosts(docData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    await fetchData();
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Blog post operations
  const addBlogPost = async (post: Omit<BlogPost, 'id' | 'created_at' | 'updated_at'>): Promise<BlogPost | null> => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .insert([post])
        .select(`
          *,
          category:categories(*)
        `)
        .single();

      if (error) throw error;

      setBlogPosts(prev => [data, ...prev]);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error adding blog post');
      return null;
    }
  };

  const updateBlogPost = async (id: string, post: Partial<BlogPost>): Promise<BlogPost | null> => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .update(post)
        .eq('id', id)
        .select(`
          *,
          category:categories(*)
        `)
        .single();

      if (error) throw error;

      setBlogPosts(prev => prev.map(p => p.id === id ? data : p));
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating blog post');
      return null;
    }
  };

  const deleteBlogPost = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setBlogPosts(prev => prev.filter(p => p.id !== id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting blog post');
      return false;
    }
  };

  const getBlogPost = (id: string) => {
    return blogPosts.find(post => post.id === id);
  };

  const getBlogPostBySlug = (slug: string) => {
    return blogPosts.find(post => post.slug === slug);
  };

  // Doc post operations
  const addDocPost = async (post: Omit<DocPost, 'id' | 'created_at' | 'updated_at'>): Promise<DocPost | null> => {
    try {
      const { data, error } = await supabase
        .from('doc_posts')
        .insert([post])
        .select(`
          *,
          category:categories(*)
        `)
        .single();

      if (error) throw error;

      setDocPosts(prev => [...prev, data].sort((a, b) => a.order - b.order));
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error adding doc post');
      return null;
    }
  };

  const updateDocPost = async (id: string, post: Partial<DocPost>): Promise<DocPost | null> => {
    try {
      const { data, error } = await supabase
        .from('doc_posts')
        .update(post)
        .eq('id', id)
        .select(`
          *,
          category:categories(*)
        `)
        .single();

      if (error) throw error;

      setDocPosts(prev => prev.map(p => p.id === id ? data : p).sort((a, b) => a.order - b.order));
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating doc post');
      return null;
    }
  };

  const deleteDocPost = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('doc_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setDocPosts(prev => prev.filter(p => p.id !== id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting doc post');
      return false;
    }
  };

  const getDocPost = (id: string) => {
    return docPosts.find(post => post.id === id);
  };

  const getDocPostBySlug = (slug: string) => {
    return docPosts.find(post => post.slug === slug);
  };

  // Category operations
  const addCategory = async (category: Omit<Category, 'id' | 'created_at' | 'updated_at'>): Promise<Category | null> => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .insert([category])
        .select()
        .single();

      if (error) throw error;

      setCategories(prev => [...prev, data].sort((a, b) => {
        if (a.type !== b.type) return a.type.localeCompare(b.type);
        return a.order - b.order;
      }));
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error adding category');
      return null;
    }
  };

  const updateCategory = async (id: string, category: Partial<Category>): Promise<Category | null> => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .update(category)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setCategories(prev => prev.map(c => c.id === id ? data : c).sort((a, b) => {
        if (a.type !== b.type) return a.type.localeCompare(b.type);
        return a.order - b.order;
      }));
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating category');
      return null;
    }
  };

  const deleteCategory = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setCategories(prev => prev.filter(c => c.id !== id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting category');
      return false;
    }
  };

  const getCategory = (id: string) => {
    return categories.find(category => category.id === id);
  };

  const getCategoriesByType = (type: 'blog' | 'doc') => {
    return categories.filter(category => category.type === type).sort((a, b) => a.order - b.order);
  };

  // Manual sitemap generation (with download)
  const generateSitemap = () => {
    const baseUrl = 'https://vmon.vn';
    const currentDate = new Date().toISOString();
    
    const staticPages = [
      { url: '/', priority: '1.0', changefreq: 'daily' },
      { url: '/vps-viet-nam', priority: '0.9', changefreq: 'weekly' },
      { url: '/vps-us', priority: '0.9', changefreq: 'weekly' },
      { url: '/vps-canada', priority: '0.9', changefreq: 'weekly' },
      { url: '/vps-europe', priority: '0.9', changefreq: 'weekly' },
      { url: '/vps-singapore', priority: '0.9', changefreq: 'weekly' },
      { url: '/vps-windows', priority: '0.9', changefreq: 'weekly' },
      { url: '/vps-forex', priority: '0.9', changefreq: 'weekly' },
      { url: '/proxy-private', priority: '0.9', changefreq: 'weekly' },
      { url: '/about', priority: '0.7', changefreq: 'monthly' },
      { url: '/terms', priority: '0.5', changefreq: 'yearly' },
      { url: '/blog', priority: '0.8', changefreq: 'daily' },
      { url: '/docs', priority: '0.8', changefreq: 'weekly' }
    ];

    // Blog URLs không có prefix /blog
    const blogUrls = blogPosts
      .filter(post => post.published)
      .map(post => ({
        url: `/${post.slug}`,
        priority: '0.7',
        changefreq: 'weekly',
        lastmod: post.updated_at
      }));

    // Doc URLs giữ nguyên prefix /docs
    const docUrls = docPosts
      .filter(doc => doc.published)
      .map(doc => ({
        url: `/docs/${doc.slug}`,
        priority: '0.8',
        changefreq: 'monthly',
        lastmod: doc.updated_at
      }));

    const allUrls = [...staticPages, ...blogUrls, ...docUrls];

    const sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${allUrls.map(page => `  <url>
    <loc>${baseUrl}${page.url}</loc>
    <lastmod>${page.lastmod || currentDate}</lastmod>
    <changefreq>${page.changefreq}</changefreq>
    <priority>${page.priority}</priority>
  </url>`).join('\n')}
</urlset>`;

    console.log('Sitemap được tạo thủ công và tải về');
    
    // Create downloadable version (chỉ khi người dùng nhấn nút)
    const blob = new Blob([sitemap], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <ContentContext.Provider value={{
      blogPosts,
      docPosts,
      categories,
      loading,
      error,
      addBlogPost,
      updateBlogPost,
      deleteBlogPost,
      getBlogPost,
      getBlogPostBySlug,
      addDocPost,
      updateDocPost,
      deleteDocPost,
      getDocPost,
      getDocPostBySlug,
      addCategory,
      updateCategory,
      deleteCategory,
      getCategory,
      getCategoriesByType,
      refreshData,
      generateSitemap
    }}>
      {children}
    </ContentContext.Provider>
  );
};